import * as React from "react";
import { userHasPermission, type PermissionId } from "@eduviaos/shared";
import { useAuth } from "@/auth/AuthProvider";

/**
 * Phase SC-22d — IA gate for the Team pay area. A UX layer only; the BE
 * re-checks every permission and the `payroll` module on each call.
 */
export interface TeamPayAccess {
  canView: boolean;
  canManage: boolean;
  canMarkAttendance: boolean;
  canApprove: boolean;
  canViewPayRuns: boolean;
  canManagePayRuns: boolean;
  /** Has no team-pay permission at all. */
  missingAll: boolean;
}

export function useTeamPayAccess(): TeamPayAccess {
  const { user } = useAuth();
  return React.useMemo<TeamPayAccess>(() => {
    if (!user) {
      return {
        canView: false,
        canManage: false,
        canMarkAttendance: false,
        canApprove: false,
        canViewPayRuns: false,
        canManagePayRuns: false,
        missingAll: false,
      };
    }
    const has = (perm: PermissionId) =>
      userHasPermission(
        user.role,
        user.permission_overrides ?? [],
        perm,
        user.permission_mode ?? "additive",
      );
    const canView = has("view_team_pay");
    const canMarkAttendance = has("manage_team_attendance");
    const canApprove = has("approve_team_attendance");
    return {
      canView,
      canManage: has("manage_team_pay"),
      canMarkAttendance,
      canApprove,
      canViewPayRuns: has("view_payroll"),
      canManagePayRuns: has("manage_payroll"),
      missingAll: !canView && !canMarkAttendance && !canApprove,
    };
  }, [user]);
}
